import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import NavigationBar from '@/components/NavigationBar';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const TransactionPage = ({ user, setUser }) => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [assetName, setAssetName] = useState('');
  const [ticketsPurchased, setTicketsPurchased] = useState(1);
  const [paymentStage, setPaymentStage] = useState('stage1'); // stage1 or stage2
  const [purchaseId, setPurchaseId] = useState(null);

  // Mock asset data with total values
  const assetPrices = {
    'lam-aventador-svj': { name: 'Lamborghini Aventador SVJ', price_per_ticket: 2500000, total_value: 30000000 },
    'ferrari-sf90': { name: 'Ferrari SF90 Stradale', price_per_ticket: 3000000, total_value: 36000000 },
    'rolls-royce-phantom': { name: 'Rolls-Royce Phantom VIII', price_per_ticket: 4500000, total_value: 54000000 },
    'mclaren-720s': { name: 'McLaren 720S', price_per_ticket: 2200000, total_value: 26400000 },
    'bentley-continental': { name: 'Bentley Continental GT', price_per_ticket: 2800000, total_value: 33600000 },
    'porsche-911-turbo': { name: 'Porsche 911 Turbo S', price_per_ticket: 1800000, total_value: 21600000 }
  };

  const asset = assetPrices[assetId] || { name: 'Asset', price_per_ticket: 0, total_value: 0 };

  // Check if coming from Asset Detail Page (Stage 1) or Dashboard (Stage 2)
  useEffect(() => {
    if (location.state?.paymentStage === 'stage2' && location.state?.purchaseId) {
      // Coming from Dashboard for Stage 2 payment
      setPaymentStage('stage2');
      setPurchaseId(location.state.purchaseId);
      setTicketsPurchased(location.state.ticketsPurchased || 1);
    } else if (location.state?.ticketsPurchased) {
      // Coming from Asset Detail Page for Stage 1 payment
      setTicketsPurchased(location.state.ticketsPurchased);
    }
  }, [location]);

  // Calculate payment amounts
  const tokenAdvancePerTicket = 10000;
  const tokenAdvance = tokenAdvancePerTicket * ticketsPurchased;
  const ticketAmount = asset.price_per_ticket * ticketsPurchased;
  const annualPlatformFee = (asset.total_value * 0.01) * ticketsPurchased;
  const stage2Amount = ticketAmount + annualPlatformFee - tokenAdvance;

  const handlePayNow = async () => {
    if (!user) {
      toast.error('Please login to proceed');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      if (paymentStage === 'stage1') {
        // Stage 1: Token Advance Payment
        const response = await axios.post(
          `${API_URL}/api/purchase/stage1`,
          {
            asset_id: assetId,
            tickets_purchased: ticketsPurchased,
            terms_accepted: termsAccepted
          },
          { withCredentials: true }
        );

        setAssetName(asset.name);
        setShowSuccessModal(true);
      } else {
        // Stage 2: Final Payment
        await axios.post(
          `${API_URL}/api/purchase/stage2`,
          {
            purchase_id: purchaseId
          },
          { withCredentials: true }
        );

        setAssetName(asset.name);
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Transaction failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate('/member-dashboard');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFBF5' }}>
      <NavigationBar user={user} />

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Back Button */}
        <Button 
          onClick={() => navigate(-1)}
          className="mb-6 border border-black bg-white text-black hover:bg-black hover:text-white transition-all"
        >
          ← Back
        </Button>

        <h1 className="text-4xl font-bold mb-2">Complete Your Purchase</h1>
        <p className="text-gray-600 mb-8">{asset.name}</p>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Left Side - Terms & Conditions (65%) */}
          <div className="md:w-[65%] bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6">Terms & Conditions</h2>
            
            <div className="space-y-4 text-gray-700 max-h-96 overflow-y-auto mb-6">
              <h3 className="font-semibold text-lg">1. Ownership Structure</h3>
              <p>By purchasing tickets in this asset through The Haute Circle fractional ownership program, you agree to become a co-owner with specific rights and responsibilities as outlined herein.</p>

              <h3 className="font-semibold text-lg">2. Payment Terms</h3>
              <p>The purchase involves four payment stages:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Stage 1 - Token Advance:</strong> ₹10,000/- payable immediately to secure your booking</li>
                <li><strong>Stage 2 - Ticket Amount + 1st Year Annual Platform Fees (APF):</strong> Ticket value plus 1% of total asset value per ticket (minus token advance), payable once all tickets are fulfilled</li>
                <li><strong>Stage 3 - Year 2 Annual Platform Fees:</strong> Payable after 360 days of owning the asset</li>
                <li><strong>Stage 4 - Year 3 Annual Platform Fees:</strong> Payable after 725 days of owning the asset</li>
              </ul>
              <p className="mt-2"><strong>Note:</strong> The token advance paid in Stage 1 will be adjusted against the Annual Platform Fee in Stage 2.</p>

              <h3 className="font-semibold text-lg">3. Usage Rights</h3>
              <p>Your ownership entitles you to scheduled usage of the asset based on your ownership percentage. Usage must be pre-booked through our scheduling system with adequate advance notice.</p>

              <h3 className="font-semibold text-lg">4. Maintenance & Insurance</h3>
              <p>All maintenance, insurance, and operational costs are included in your ownership and managed by The Haute Circle. No additional charges apply for regular usage within your allocated time.</p>

              <h3 className="font-semibold text-lg">5. Cancellation Policy</h3>
              <p>Token advance is non-refundable once the booking is confirmed. Subsequent instalments are subject to our cancellation policy with potential deductions for administrative costs.</p>

              <h3 className="font-semibold text-lg">6. Transfer of Ownership</h3>
              <p>Ownership tickets may be transferred to another party subject to approval by The Haute Circle and completion of necessary legal formalities. Transfer fees may apply.</p>

              <h3 className="font-semibold text-lg">7. Asset Maintenance</h3>
              <p>The Haute Circle reserves the right to schedule maintenance periods during which the asset will not be available for use. Users will be notified in advance.</p>

              <h3 className="font-semibold text-lg">8. Liability</h3>
              <p>Users are responsible for any damage caused during their usage period beyond normal wear and tear. Comprehensive insurance covers major incidents.</p>

              <h3 className="font-semibold text-lg">9. Dispute Resolution</h3>
              <p>Any disputes arising from this agreement shall be resolved through arbitration in accordance with Indian law, with jurisdiction in Mumbai courts.</p>

              <h3 className="font-semibold text-lg">10. Agreement Duration</h3>
              <p>This fractional ownership agreement is valid for the duration specified in your purchase documentation and is renewable subject to terms.</p>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Checkbox
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                id="terms"
                className="mt-1"
              />
              <label htmlFor="terms" className="text-sm cursor-pointer">
                I have read and accept the Terms & Conditions outlined above and agree to proceed with the purchase
              </label>
            </div>
          </div>

          {/* Right Side - Payment Breakdown (35%) */}
          <div className="md:w-[35%]">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-6">
              <h2 className="text-xl font-bold mb-6">Payment Breakdown</h2>

              <div className="space-y-6">
                {/* Stage 1: Token Advance */}
                <div className={`p-4 rounded-lg ${paymentStage === 'stage1' ? 'bg-gold/10 border-2 border-gold' : 'bg-green-50 border-2 border-green-500'}`}>
                  <p className="text-sm text-gray-600 mb-1">Stage 1 - Token Advance</p>
                  <p className={`text-3xl font-bold ${paymentStage === 'stage1' ? 'text-gold' : 'text-green-600'}`}>
                    ₹{tokenAdvance.toLocaleString('en-IN')}/-
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    {paymentStage === 'stage1' ? 'Payable Now' : '✓ Paid'}
                  </p>
                </div>

                {/* Stage 2: Ticket Amount + APF */}
                <div className={`p-4 rounded-lg ${paymentStage === 'stage2' ? 'bg-gold/10 border-2 border-gold' : 'bg-gray-100 opacity-60'}`}>
                  <p className={`text-sm mb-1 ${paymentStage === 'stage2' ? 'text-gray-600' : 'text-gray-500'}`}>
                    Stage 2 - Ticket Amount + 1st Year APF
                  </p>
                  <p className={`text-xl font-semibold ${paymentStage === 'stage2' ? 'text-gold' : 'text-gray-400'}`}>
                    ₹{stage2Amount.toLocaleString('en-IN')}/-
                  </p>
                  <div className="text-xs mt-2 space-y-1">
                    <div className="flex justify-between">
                      <span className={paymentStage === 'stage2' ? 'text-gray-600' : 'text-gray-400'}>Ticket Amount:</span>
                      <span className={paymentStage === 'stage2' ? 'text-gray-700' : 'text-gray-400'}>₹{ticketAmount.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={paymentStage === 'stage2' ? 'text-gray-600' : 'text-gray-400'}>APF (1% of asset):</span>
                      <span className={paymentStage === 'stage2' ? 'text-gray-700' : 'text-gray-400'}>₹{annualPlatformFee.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between border-t pt-1">
                      <span className={paymentStage === 'stage2' ? 'text-gray-600' : 'text-gray-400'}>Token Advance:</span>
                      <span className={paymentStage === 'stage2' ? 'text-gray-700' : 'text-gray-400'}>- ₹{tokenAdvance.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    {paymentStage === 'stage2' ? 'Payable Now' : 'Once All Tickets Fulfilled'}
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Total Asset Value:</span>
                  <span className="font-semibold">₹{asset.total_value.toLocaleString('en-IN')}/-</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 mb-2">
                  <span>Tickets Purchasing:</span>
                  <span>{ticketsPurchased}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Token Adjustable:</span>
                  <span>Yes (in Stage 2)</span>
                </div>
              </div>

              <Button
                onClick={handlePayNow}
                disabled={!termsAccepted || loading}
                className="w-full mt-6 bg-black text-white border-2 border-black hover:bg-white hover:text-black py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Pay Now'}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Secure payment processing • Token advance is adjustable against total amount
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center shadow-2xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-3">Payment Successful!</h3>
            <p className="text-gray-600 mb-2">Thank you, {user?.first_name} {user?.last_name}!</p>
            {paymentStage === 'stage1' ? (
              <>
                <p className="text-gray-600 mb-6">
                  Your token advance for <strong>{assetName}</strong> has been processed successfully.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Your booking is secured! Visit your dashboard to track the status and proceed with Stage 2 payment once all tickets are fulfilled.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-6">
                  Congratulations! Your purchase for <strong>{assetName}</strong> is complete.
                </p>
                <p className="text-sm text-gray-500 mb-2">
                  <strong>Asset Deployment Timeline:</strong> 7 days
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  The asset will be registered and deployed within 7 days. You'll be able to schedule usage once deployment is complete.
                </p>
              </>
            )}
            <Button 
              onClick={handleCloseSuccessModal}
              className="bg-gold hover:bg-gold/90 text-black font-semibold px-8"
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionPage;
